import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { ExportMenu } from "@/components/explorer/ExportMenu";
import { BulkBar, SelectAllBox, SelectBox, useSelection } from "@/components/shared/BulkSelect";
import { NewDot, HealthChip, useRowSignals } from "@/components/shared/RowSignals";
import { healthOf } from "@/lib/health";
import { labelOf } from "@/lib/fieldEnums";
import { toDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const ms = (v) => toDate(v)?.getTime() || 0;

// Generic Super CRM list: search, status + health filters, row dots, flashing
// critical/stuck rows, checkbox selection with multi-field bulk update, export,
// full-record editor per row. columns: [{ key, label, cell(doc), className, sort?(doc) }]
export function CrmList({ path, rows, columns, statusField = "status", statuses = [], searchText, onOpen, bulkFields, bulkActions, bulkName, section = "crm", defaultSort, empty = "Nothing here yet.", toolbarExtra, rowExtra }) {
  const [q, setQ] = useState("");
  const [st, setSt] = useState("all");
  const [onlyHot, setOnlyHot] = useState(false);
  const [sort, setSort] = useState(defaultSort || null);
  const sig = useRowSignals(path, section);

  const statusOpts = useMemo(() => Array.from(new Set([...statuses, ...rows.map((r) => r[statusField]).filter(Boolean)])), [rows, statuses, statusField]);
  const shown = useMemo(() => {
    let out = rows.filter((r) => (st === "all" || (r[statusField] || "") === st)
      && (!onlyHot || healthOf(path, r))
      && (!q || (JSON.stringify(r) + " " + (searchText ? searchText(r) : "")).toLowerCase().includes(q.toLowerCase())));
    const col = sort && columns.find((c) => c.key === sort.key);
    if (col?.sort) out = [...out].sort((a, b) => { const x = col.sort(a), y = col.sort(b); return (x > y ? 1 : x < y ? -1 : 0) * (sort.dir === "desc" ? -1 : 1); });
    else out = [...out].sort((a, b) => Math.max(ms(b.updated_at), ms(b.created_at)) - Math.max(ms(a.updated_at), ms(a.created_at)));
    // Hot rows (critical first, then stuck) float to the top when not sorting explicitly.
    if (!col) out.sort((a, b) => rank(healthOf(path, a)) - rank(healthOf(path, b)));
    return out;
  }, [rows, st, onlyHot, q, sort, columns, path, statusField, searchText]);
  const s = useSelection(shown);
  const hot = rows.filter((r) => healthOf(path, r)).length;

  const clickSort = (c) => c.sort && setSort((cur) => (cur?.key === c.key ? (cur.dir === "asc" ? { key: c.key, dir: "desc" } : null) : { key: c.key, dir: "asc" }));

  return (
    <div>
      <div className="p-3 sm:p-4 border-b border-border flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project, customer, remark…" className="h-9 pl-9" data-testid={`crm-search-${path}`} />
        </div>
        <Select value={st} onValueChange={setSt}>
          <SelectTrigger className="h-9 w-48 text-xs" data-testid={`crm-status-${path}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All {statusField === "status" ? "statuses" : statusField.replace(/_/g, " ")}</SelectItem>
            {statusOpts.map((x) => <SelectItem key={x} value={x} className="text-xs">{labelOf(x)} · {rows.filter((r) => r[statusField] === x).length}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant={onlyHot ? "destructive" : "outline"} className="h-9" onClick={() => setOnlyHot(!onlyHot)} data-testid={`crm-hot-${path}`}>
          <Icons.Siren className="h-3.5 w-3.5 mr-1" />Critical / stuck{hot ? ` (${hot})` : ""}
        </Button>
        {toolbarExtra}
        <span className="text-xs text-muted-foreground font-mono ml-auto">{shown.length} shown</span>
        <ExportMenu path={path} shown={shown} />
      </div>
      <div className="px-3 pt-3"><BulkBar s={s} path={path} rows={rows} fields={bulkFields} actions={bulkActions || []} {...(bulkName ? { nameOf: bulkName } : {})} /></div>
      <div className="overflow-x-auto">
        <Table data-testid={`crm-table-${path}`}>
          <TableHeader><TableRow>
            <TableHead className="w-8"><SelectAllBox s={s} testId={`crm-select-all-${path}`} /></TableHead>
            <TableHead className="w-4 px-0" />
            {columns.map((c) => (
              <TableHead key={c.key} className={cn(c.headClass, c.sort && "cursor-pointer select-none")} onClick={() => clickSort(c)}>
                <span className="inline-flex items-center gap-1">{c.label}{sort?.key === c.key && (sort.dir === "asc" ? <Icons.ArrowUp className="h-3 w-3" /> : <Icons.ArrowDown className="h-3 w-3" />)}</span>
              </TableHead>
            ))}
            <TableHead className="w-10" />
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((r) => {
              const h = healthOf(path, r);
              return (
                <TableRow key={r.id} className={cn("cursor-pointer align-top", sig.rowClass(r), s.sel.has(r.id) && "bg-primary/5")} onClick={() => { sig.opened(r.id); onOpen && onOpen(r.id); }} data-testid={`crm-row-${r.id}`}>
                  <TableCell className="pt-3.5"><SelectBox s={s} id={r.id} /></TableCell>
                  <TableCell className="px-0 pt-4"><NewDot show={sig.isNew(r.id)} /></TableCell>
                  {columns.map((c, i) => (
                    <TableCell key={c.key} className={c.className}>
                      {c.cell(r)}
                      {i === 0 && h && <div className="mt-1"><HealthChip h={h} /></div>}
                      {i === 0 && rowExtra && rowExtra(r)}
                    </TableCell>
                  ))}
                  <TableCell onClick={(e) => e.stopPropagation()}><EditRecordButton path={path} id={r.id} /></TableCell>
                </TableRow>
              );
            })}
            {shown.length === 0 && <TableRow><TableCell colSpan={columns.length + 3} className="text-center py-12 text-muted-foreground">{empty}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const rank = (h) => (h?.level === "critical" ? 0 : h?.level === "stuck" ? 1 : 2);
